import { create } from 'zustand';

// Imperative toaster store. The single global `<Toaster>` mounted in the
// root layout subscribes to this store and renders whatever's current.
// Imperative API (`toastStore.show(...)`) lets non-React callers — like
// the panel core's `handleServerStep` — push toasts without threading
// hooks through every layer.
//
// One toast at a time keeps the UX simple: a new `show()` replaces the
// current toast, so back-to-back agent actions don't stack chips at the
// bottom of the page.

export type ToastAction = {
  label: string;
  onClick: () => void;
};

/**
 * Where the toast is pinned. Defaults to the historical bottom-centre position;
 * `top-right` is opt-in so adding it cannot move any existing caller's toast.
 */
export type ToastPlacement = 'bottom-center' | 'top-right';

// Optional visual tone. Omitted / `default` keeps the historical dark chip
// so existing callers do not change colour. `error` / `success` match the
// legacy sonner error/success toasts (XT-334 v2 parity).
export type ToastTone = 'default' | 'error' | 'success';

export type Toast = {
  /** Optional emphasised first line. Existing callers pass only `message`. */
  title?: string;
  tone?: ToastTone;
  // Unique id used to dismiss the same toast even if a later `show()` raced
  // its timeout — prevents the second toast getting torn down by the first
  // toast's stale auto-dismiss timer.
  id: number;
  message: string;
  action?: ToastAction;
  // `true` keeps the toast visible until the caller explicitly dismisses
  // it. Used for long-running progress indicators (PDF/PPTX export) where
  // the heavy steps can block paint for >TOAST_AUTO_DISMISS_MS and would
  // otherwise let the timer fire mid-export.
  sticky?: boolean;
  placement?: ToastPlacement;
};

type ToastStore = {
  current: Toast | null;
  show: (toast: Omit<Toast, 'id'>) => number;
  dismiss: (id: number) => void;
};

let nextId = 1;

export const toastStore = create<ToastStore>((set, get) => ({
  current: null,
  show: (input) => {
    const id = nextId++;
    set({ current: { id, ...input } });
    return id;
  },
  dismiss: (id) => {
    if (get().current?.id === id) set({ current: null });
  },
}));

export const TOAST_AUTO_DISMISS_MS = 6_000;
